import {
  useImmediateExpiringConditions,
  PreBookConfirmationDialog,
  RecommendedSpecialtiesModal,
  usePerDiemEventTracking,
  useSpecialtyTracking,
  useProfessionalQualifications,
  useRecommendedSpecialtiesData,
  JobPositionsProps,
  MissingHighRiskSpecialtiesProps,
} from '@mp';
import { useAnalytics } from '@medely/ui-kit';
import { Button } from '@medely/web-components';
import { Lock } from '@medely/web-components/icons';
import { PerDiemFiltersProvider } from 'context/filters/perDiemFiltersContext';
import useCurrentUser from 'hooks/useCurrentUser';
import useDialog from 'hooks/useDialog';
import useMissingOnboardingConditions from 'hooks/useMissingOnboardingConditions';
import usePerDiemJobsSearch from 'hooks/usePerDiemJobsSearch';
import { useTrackingQueryParams } from 'hooks/useTrackingQueryParams';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { axios } from 'utils/networkRequests';
import { IJob } from '@medely/types';
import { JobStatus as MpJobStatus } from '@mp/graphql/generated/graphql';

type IJobWithMissingHighRiskSpecialties = IJob & {
  missing_high_risk_specialties?: MissingHighRiskSpecialtiesProps[] | null;
};

interface IBookJobContainerProps {
  job?: IJobWithMissingHighRiskSpecialties;
  source?: string;
  disabled?: boolean;
}

const BookJobComponent = ({ job, source = 'book', disabled = false }: IBookJobContainerProps) => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state: { book: boolean | undefined } | null };
  const { currentUser } = useCurrentUser();

  const { userHasAllRequiredSpecialties, specialtyOptions } = useRecommendedSpecialtiesData({
    jobPositions: job?.positions as JobPositionsProps[],
    jobSpecialties: job?.specialties,
    jobMissingHighRiskSpecialties: job?.missing_high_risk_specialties,
    userSpecialties: currentUser?.professional?.specialties,
  });

  const analytics = useAnalytics();
  const { trackPromptedForSpecialtyRequirements } = useSpecialtyTracking(analytics);
  const autoBook = !!state?.book;
  const { immediateExpiringConditionIds, isLoading } = useImmediateExpiringConditions(
    job?.current_starts_time,
    job?.id,
    job?.status as MpJobStatus | undefined,
  );
  const canBookJob = (job?.can_book_job && !immediateExpiringConditionIds.length) || false;
  const requiresFacilityOnboarding = job?.requires_sca_onboarding_condition;
  const { completedFacilityOnboarding } = useProfessionalQualifications({
    job_id: job?.id,
  });

  const { open, openDialog } = useDialog(canBookJob && autoBook);
  const recommendedSpecialtiesModal = useDialog();
  const { bookJob, bookingLoading, bookJobWithAttestation, bookAndAttestLoading } =
    usePerDiemJobsSearch();
  const { queryParams } = useTrackingQueryParams();
  const { conditions: missingW2Conditions } = useMissingOnboardingConditions({ is_w2: true });
  const { trackShiftDetailsReview } = usePerDiemEventTracking(analytics);

  const handleUnlock = () => {
    navigate(`/available/jobs/${job?.id}/credentials`, {
      state: { immediateExpiringConditionIds },
    });
  };

  const handleW2Unlock = () => {
    navigate(`/jobs/${job?.id}/onboarding/w2`);
  };

  const openDialogWithTracking = () => {
    axios.post(`v3/professional/jobs/${job?.id}/tracking/confirmation_viewed`, {
      queryParams: { ...queryParams, source: source },
    });
    trackShiftDetailsReview(job?.id);

    if (userHasAllRequiredSpecialties) {
      openDialog();
    } else {
      trackPromptedForSpecialtyRequirements(currentUser?.professional?.id);
      recommendedSpecialtiesModal.openDialog();
    }
  };

  const openFacilityOnboarding = () => {
    navigate(`/available/jobs/${job?.id}/onboarding`);
  };

  const getBookText = () => {
    if (source === 'book') {
      return 'Book';
    } else if (requiresFacilityOnboarding || source === 'facility_confirmation') {
      return 'Accept & Book';
    }
    return 'Book';
  };

  const onClose = () => {
    navigate(`/jobs/${job?.id}`, { replace: true, state: { openPostBookModal: true } });
  };

  let button;

  if (job?.is_w2 && !!missingW2Conditions.length) {
    button = (
      <Button
        color="primary"
        data-testid="book-job-button-w2-unlock-button"
        fullWidth
        onClick={handleW2Unlock}
        startIcon={<Lock />}
        variant="contained"
      >
        Unlock W-2 shifts
      </Button>
    );
  } else if (canBookJob) {
    button = (
      <Button
        color="primary"
        data-testid="book-job-button-book-button"
        disabled={bookingLoading || isLoading || disabled}
        fullWidth
        loading={isLoading}
        onClick={
          requiresFacilityOnboarding && !completedFacilityOnboarding && source === 'book'
            ? openFacilityOnboarding
            : openDialogWithTracking
        }
        variant="contained"
      >
        {getBookText()}
      </Button>
    );
  } else {
    button = (
      <Button
        color="primary"
        data-testid="book-job-button-unlock-button"
        fullWidth
        onClick={handleUnlock}
        startIcon={<Lock />}
        variant="contained"
      >
        Unlock
      </Button>
    );
  }

  return (
    <>
      {button}
      <PreBookConfirmationDialog
        open={open}
        onClose={onClose}
        job={job as unknown as Parameters<typeof PreBookConfirmationDialog>[0]['job']}
        bookJob={bookJob}
        bookingLoading={bookingLoading}
        bookJobWithAttestation={bookJobWithAttestation}
        bookAndAttestLoading={bookAndAttestLoading}
        userCoordinates={currentUser?.coordinates}
        onboarding={requiresFacilityOnboarding && !completedFacilityOnboarding}
        disabled={disabled}
      />
      <RecommendedSpecialtiesModal
        open={recommendedSpecialtiesModal.open}
        onClose={() => recommendedSpecialtiesModal.closeDialog()}
        options={specialtyOptions}
        handleContinueClick={() => {
          recommendedSpecialtiesModal.closeDialog();
          openDialog();
        }}
      />
    </>
  );
};

const BookJobContainer = ({ job, source = 'book', disabled = false }: IBookJobContainerProps) => (
  <PerDiemFiltersProvider>
    <BookJobComponent job={job} source={source} disabled={disabled} />
  </PerDiemFiltersProvider>
);

export default BookJobContainer;
